// The world clock and weather. Both are worked out from real time, so every server and every player agrees
// without sending anything: the same sunset, the same rain, the same townsfolk out at night.
// A developer can pin the hour or the weather for testing (the server tells everyone).

/** One full day and night takes this long in real time: 48 minutes, so 2 real minutes per game hour. */
export const DAY_MS = 48 * 60 * 1000;
/** Weather holds for this long, then may change. */
export const WEATHER_MS = 20 * 60 * 1000;
const EPOCH = Date.UTC(2026, 0, 1);

export type Weather = 'clear' | 'cloudy' | 'rain' | 'storm' | 'fog';
export const WEATHERS: Weather[] = ['clear', 'cloudy', 'rain', 'storm', 'fog'];
export interface SkyOverride { hour: number | null; weather: Weather | null }

/** Game hour, 0 to 24 (fractional). The world starts each real day at a different hour, on purpose. */
export function hourAt(ms: number, o?: SkyOverride | null): number {
  if (o?.hour != null) return o.hour;
  return (((ms - EPOCH) / DAY_MS) * 24 + 8) % 24;
}

export type Phase = 'dawn' | 'day' | 'dusk' | 'night';
export function phaseOf(hour: number): Phase {
  return hour >= 5 && hour < 7 ? 'dawn' : hour >= 7 && hour < 18.5 ? 'day' : hour >= 18.5 && hour < 20.5 ? 'dusk' : 'night';
}
export const isNight = (hour: number) => hour >= 20.5 || hour < 5;

function hash(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
const zoneSeed = (zone: string) => { let h = 7; for (let i = 0; i < zone.length; i++) h = (h * 31 + zone.charCodeAt(i)) | 0; return h; };

/** The weather in a zone during one 20-minute slot. Clear most of the time, rain now and then. */
function weatherForSlot(zone: string, slot: number): Weather {
  const r = hash(zoneSeed(zone), slot);
  return r < 0.5 ? 'clear' : r < 0.72 ? 'cloudy' : r < 0.9 ? 'rain' : r < 0.95 ? 'storm' : 'fog';
}

export function weatherAt(zone: string, ms: number, o?: SkyOverride | null): Weather {
  if (o?.weather) return o.weather;
  return weatherForSlot(zone, Math.floor((ms - EPOCH) / WEATHER_MS));
}

/** True while it's raining or for 30 minutes after (rain caps grow then). */
export function recentlyRained(zone: string, ms: number, o?: SkyOverride | null): boolean {
  if (o?.weather) return o.weather === 'rain' || o.weather === 'storm';
  for (const back of [0, 30 * 60 * 1000]) { const w = weatherAt(zone, ms - back); if (w === 'rain' || w === 'storm') return true; }
  return false;
}

/** How far into the current weather slot we are (0..1), for blending changes on screen. */
export function weatherProgress(ms: number): number { return (((ms - EPOCH) % WEATHER_MS) + WEATHER_MS) % WEATHER_MS / WEATHER_MS; }
export function previousWeather(zone: string, ms: number): Weather { return weatherForSlot(zone, Math.floor((ms - EPOCH) / WEATHER_MS) - 1); }

/** "8:45 pm" for the clock on screen. */
export function clockText(hour: number): string {
  const h = Math.floor(hour), m = Math.floor((hour - h) * 60 / 15) * 15, h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}
